import type { IODataFormState, IODataFilterRow, ODataOperator } from "../../declarations/ODataExplorer";

const TOP_DEFAULT = 50;

const VALID_OPERATORS: ReadonlySet<ODataOperator> = new Set([
    "eq", "ne", "gt", "ge", "lt", "le",
    "contains", "startswith", "endswith",
    "is null", "is not null",
]);

export function defaultFormState(): IODataFormState {
    return {
        entity: "",
        select: [],
        filters: [],
        orderByProperty: "",
        orderByDescending: false,
        top: TOP_DEFAULT,
        skip: 0,
        companies: [],
        runId: 0,
    };
}

const escapeComma = (s: string) => s.replace(/\\/g, "\\\\").replace(/,/g, "\\,");
const unescapeComma = (s: string) => s.replace(/\\,/g, ",").replace(/\\\\/g, "\\");

function splitUnescaped(value: string): string[] {
    if (value === "") return [];
    const out: string[] = [];
    let buf = "";
    for (let i = 0; i < value.length; i++) {
        if (value[i] === "\\" && value[i + 1] === ",") {
            buf += "\\,";
            i++;
        } else if (value[i] === ",") {
            out.push(buf);
            buf = "";
        } else {
            buf += value[i];
        }
    }
    out.push(buf);
    return out;
}

function serializeFilter(row: IODataFilterRow): string {
    const value = row.value ? escapeComma(row.value) : "";
    return value ? `${row.property}:${row.operator}:${value}` : `${row.property}:${row.operator}`;
}

function parseFilter(s: string): IODataFilterRow {
    const firstColon = s.indexOf(":");
    if (firstColon < 0) return { id: crypto.randomUUID(), property: s, operator: "eq", value: "" };
    const secondColon = s.indexOf(":", firstColon + 1);
    const op = s.slice(firstColon + 1, secondColon < 0 ? undefined : secondColon) as ODataOperator;
    const operator: ODataOperator = VALID_OPERATORS.has(op) ? op : "eq";
    if (secondColon < 0) {
        return {
            id: crypto.randomUUID(),
            property: s.slice(0, firstColon),
            operator,
            value: "",
        };
    }
    return {
        id: crypto.randomUUID(),
        property: s.slice(0, firstColon),
        operator,
        value: unescapeComma(s.slice(secondColon + 1)),
    };
}

export function formStateToParams(state: IODataFormState): URLSearchParams {
    const p = new URLSearchParams();
    if (state.entity) p.set("entity", state.entity);
    if (state.select.length) p.set("select", state.select.join(","));
    if (state.filters.length) p.set("filter", state.filters.map(serializeFilter).join(","));
    if (state.orderByProperty) p.set("orderby", `${state.orderByProperty}:${state.orderByDescending ? "desc" : "asc"}`);
    if (state.top !== TOP_DEFAULT) p.set("top", String(state.top));
    if (state.skip > 0) p.set("skip", String(state.skip));
    if (state.companies.length) p.set("companies", state.companies.join(","));
    return p;
}

export function paramsToFormState(p: URLSearchParams): IODataFormState {
    const state = defaultFormState();
    state.entity = p.get("entity") ?? "";
    const sel = p.get("select"); if (sel) state.select = sel.split(",").filter(Boolean);
    const flt = p.get("filter"); if (flt) state.filters = splitUnescaped(flt).map(parseFilter);
    const ord = p.get("orderby");
    if (ord) {
        const [prop, dir] = ord.split(":");
        state.orderByProperty = prop ?? "";
        state.orderByDescending = dir === "desc";
    }
    const top = p.get("top");
    if (top) {
        const n = Number(top);
        state.top = Number.isFinite(n) && n > 0 ? n : TOP_DEFAULT;
    }
    const skip = p.get("skip");
    if (skip) {
        const n = Number(skip);
        state.skip = Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
    }
    const co = p.get("companies"); if (co) state.companies = co.split(",").filter(Boolean);
    return state;
}
